// Import the same components and hooks as Home.tsx
// This file will contain the functionality that was previously in Home.tsx but with a different name
import { 
  Box, 
  Button, 
  Flex, 
  Heading, 
  Text, 
  VStack,
  HStack,
  Image, 
  Divider,
  useColorModeValue,
  Skeleton,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Code,
  useDisclosure,
  useToast,
  Link,
  Icon
} from '@chakra-ui/react';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useSpotify } from '../context/SpotifyContext';
import { useAppleMusic } from '../context/AppleMusicContext';
import { useMusicService, MusicServiceType } from '../context/MusicServiceContext';
import { useProgress } from '../context/ProgressContext';
import { spotifyService } from '../services/spotifyService';
import { appleMusicService } from '../services/appleMusicService';
import Lyrics from '../components/Lyrics';
import LyricsSettingsModal from '../components/LyricsSettingsModal';
import { useLyricsTheme } from '../context/LyricsThemeContext';
import SideBySideLayout from '../components/layouts/SideBySideLayout';
import TeslaLayout from '../components/layouts/TeslaLayout';
import Background from '../components/Background';
import AppHeader from '../components/AppHeader';
import MusicServiceButtons from '../components/MusicServiceButtons';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MusicServiceToggle from '../components/MusicServiceToggle';
import { FaMusic } from 'react-icons/fa';
import AuthModal from '../components/AuthModal';
import { FiRefreshCw } from 'react-icons/fi';
import { trackSongPlay, trackLyricsView, trackFeatureUsage, trackButtonClick, trackError } from '../utils/eventTracking';

// Create a simple skeleton component directly in this file
const LayoutSkeleton = ({ layoutMode }: { layoutMode: 'split' | 'tesla' }) => {
  return (
    <Box height="calc(100vh - 70px)" width="100%" position="relative">
      {layoutMode === 'split' ? (
        <Flex direction={{ base: 'column', md: 'row' }} height="100%">
          <Box 
            width={{ base: '100%', md: '40%' }} 
            height={{ base: '40%', md: '100%' }}
            p={4}
          >
            <Skeleton 
              height="70%" 
              width="70%" 
              mx="auto" 
              my={10}
              borderRadius="md"
            />
          </Box>
          <Box 
            width={{ base: '100%', md: '60%' }} 
            height={{ base: '60%', md: '100%' }}
            p={8}
          >
            <Skeleton height="20px" width="80%" mb={4} />
            <Skeleton height="20px" width="90%" mb={4} />
            <Skeleton height="20px" width="85%" mb={4} />
            <Skeleton height="20px" width="70%" mb={4} />
          </Box>
        </Flex>
      ) : (
        <Box height="100%" p={6}>
          <Box height="70%" overflowY="auto" p={4}>
            <Skeleton height="30px" width="80%" mb={6} />
            <Skeleton height="30px" width="90%" mb={6} />
            <Skeleton height="30px" width="85%" mb={6} />
            <Skeleton height="30px" width="75%" mb={6} />
          </Box>
          <Box 
            position="absolute" 
            bottom="0" 
            left="0" 
            right="0" 
            height="130px" 
            bg="rgba(0,0,0,0.2)"
            p={4}
          >
            <Flex>
              <Skeleton height="80px" width="80px" mr={4} />
              <Box flex="1">
                <Skeleton height="24px" width="200px" mb={2} />
                <Skeleton height="16px" width="150px" mb={4} />
                <Skeleton height="10px" width="100%" />
              </Box>
            </Flex>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Add helper text to explain login status
const renderAuthStatus = (serviceName: string, isAuthenticating: boolean, errorMessage?: string) => {
  if (isAuthenticating) {
    return (
      <Text fontSize="sm" color="orange.400" mt={2}>
        Authenticating with {serviceName}...
      </Text>
    );
  }

  if (errorMessage) {
    return (
      <Text fontSize="sm" color="red.400" mt={2}>
        {errorMessage}
      </Text>
    );
  }

  return null;
};

const Player = () => {
  // IMPORTANT: SyncdIt does not stream or play any audio content directly.
  // This component only synchronizes with the official Spotify/Apple Music services
  // to display lyrics timed with the music playing through their official apps.
  // All interactions comply with the respective music service's developer terms.
  
  // Use both Spotify and Apple Music contexts
  const { 
    isAuthenticated: isSpotifyAuthenticated, 
    accessToken: spotifyToken, 
    login: spotifyLogin, 
    logout: spotifyLogout,
    tokenExpired: spotifyTokenExpired,
    refreshToken: refreshSpotifyToken
  } = useSpotify();
  const { 
    isAuthenticated: isAppleAuthenticated, 
    instance: appleMusicInstance, 
    login: appleLogin, 
    logout: appleLogout, 
    isInitializing: isAppleMusicInitializing,
    userStorefront
  } = useAppleMusic();
  
  // Music service selection context
  const { currentService, setCurrentService, isSpotifySelected, isAppleSelected } = useMusicService();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Progress context
  const { 
    progressMs, 
    setProgressMs, 
    startProgressTimer,
    resetForNewTrack,
    resetTimingForSameTrack,
    manualSelectedLyric, 
    manualLyricTimestamp, 
    setManualLyricSelection,
    userScrolling, 
    setUserScrolling,
    isPlaying,
    currentTrackId,
    setCurrentTrackId,
    clearManualStates,
    lyricsDelay,
    isInManualMode,
    getEffectiveProgress,
    setLyricsDelay
  } = useProgress();
  
  // Get Supabase authentication status
  const { user } = useAuth();
  
  // Check if user is authenticated with Supabase
  const isSupabaseAuthenticated = !!user;
  
  // Determine if authenticated based on selected service
  const isMusicServiceAuthenticated = isSpotifySelected ? isSpotifyAuthenticated : isAppleAuthenticated;
  
  // Use Supabase auth as the primary authentication check
  const isAuthenticated = isSupabaseAuthenticated;
  
  // Authentication status tracking
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Service-specific states
  const [playbackState, setPlaybackState] = useState<any>(null);
  const [lyrics, setLyrics] = useState<any>(null);
  const [currentLyricLine, setCurrentLyricLine] = useState<string>('');
  const [currentLyricTimestamp, setCurrentLyricTimestamp] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isChangingSong, setIsChangingSong] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const currentLineRef = useRef<HTMLDivElement>(null);
  const [queueData, setQueueData] = useState<any>(null);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // Get color mode and background settings from LyricsThemeContext
  const { 
    colorMode, 
    isDarkMode, 
    isLightMode, 
    backgroundType,
    frostedEffect,
    frostedOpacity
  } = useLyricsTheme();
  
  // UI colors
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const accentColor = useColorModeValue(isSpotifySelected ? 'green.500' : 'pink.500', isSpotifySelected ? 'green.300' : 'pink.300');
  const lyricsHighlightColor = useColorModeValue(isSpotifySelected ? 'green.500' : 'pink.500', isSpotifySelected ? 'green.300' : 'pink.300');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');

  // Add layout state
  const [layoutMode, setLayoutMode] = useState<'split' | 'tesla'>(() => {
    // Try to get saved layout from localStorage
    const savedLayout = localStorage.getItem('currentLayout');
    // Return saved layout if it exists, otherwise default to 'split'
    return (savedLayout === 'tesla' || savedLayout === 'split') ? savedLayout : 'split';
  });
  
  // Save layout changes to localStorage
  useEffect(() => {
    localStorage.setItem('currentLayout', layoutMode);
    console.log('Layout mode saved to localStorage:', layoutMode);
  }, [layoutMode]);

  // Add state for tracking minimum API latency
  const [minApiLatency, setMinApiLatency] = useState<number>(() => {
    // Try to get saved minimum latency from localStorage
    const savedLatency = localStorage.getItem('spotify_min_api_latency');
    return savedLatency ? parseInt(savedLatency, 10) : 500; // Default to 500ms if not found
  });
  
  // Save minimum latency to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('spotify_min_api_latency', minApiLatency.toString());
    // console.log('Updated minimum API latency:', minApiLatency, 'ms');
  }, [minApiLatency]);

  // Login/logout handlers based on selected service
  const login = async () => {
    setIsAuthenticating(true);
    setAuthError(null);
    
    try {
      if (isSpotifySelected) {
        await spotifyLogin();
        trackFeatureUsage('Login', 'Spotify');
      } else if (isAppleSelected) {
        await appleLogin();
        trackFeatureUsage('Login', 'Apple Music');
      }
    } catch (error) {
      trackError('Music Service Auth', 'Failed to authenticate', false);
      setAuthError('Failed to authenticate. Please try again.');
      console.error('Authentication error:', error);
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Add supabase auth hook
  const { signOut: supabaseSignOut } = useAuth();

  // Add navigation hook
  const navigate = useNavigate();

  // Add toast hook
  const toast = useToast();

  // Define logout function with try-catch to handle errors
  const handleLogout = async () => {
    try {
      if (isSpotifySelected) {
        await spotifyLogout();
        trackFeatureUsage('Logout', 'Spotify');
      } else if (isAppleSelected) {
        await appleLogout();
        trackFeatureUsage('Logout', 'Apple Music');
      }
      
      // Then log out from Supabase
      await supabaseSignOut();
      
      // Navigate to home page after successful logout
      navigate('/');
      
      console.log('Logged out successfully from all services');
    } catch (error) {
      trackError('Music Service Auth', 'Failed to logout', false);
      console.error('Error during logout:', error);
      toast({
        title: 'Logout Error',
        description: 'An error occurred during logout. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Add a new effect to monitor token expiration
  useEffect(() => {
    // If the Spotify token has expired and the user is using Spotify
    if (isSpotifySelected && spotifyTokenExpired) {
      console.log('Detected expired Spotify token in Player');
      setAuthError('Your Spotify session has expired. Please log in again.');
    }
  }, [isSpotifySelected, spotifyTokenExpired]);

  // When service changes, reset auth error
  useEffect(() => {
    setAuthError(null);
  }, [currentService]);

  // Switch between services
  const handleServiceChange = (service: MusicServiceType) => {
    setCurrentService(service);
    // Reset state when changing services
    setPlaybackState(null);
    setLyrics(null);
    setCurrentLyricLine('');
    setCurrentLyricTimestamp(undefined);
    setQueueData(null);
    setCurrentTrackId(null);
    
    // Reset tracking in the services
    if (service === 'spotify') {
      spotifyService.resetCurrentTrack();
    } else if (service === 'apple') {
      appleMusicService.resetCurrentTrack();
    }
    clearManualStates();
    trackFeatureUsage('Service Change', service);
  };

  // Format milliseconds to MM:SS
  const formatDuration = (ms: number) => {
    if (!ms) return '0:00';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Function to handle manual lyric line selection
  const handleManualLyricSelect = (lineText: string, timestamp: number) => {
    // When a lyric is manually selected:
    // 1. Set the manual selection state
    setManualLyricSelection(lineText, timestamp);
    
    // 2. If playing, continue tracking from this new position
    if (playbackState?.is_playing) {
      startProgressTimer(timestamp, true);
    }
    
    console.log(`Manual selection: "${lineText}" at ${timestamp}ms`);
    setUserScrolling(false);
    trackFeatureUsage('Manual Lyric Selection');
  };
  
  // Function to handle clearing manual selection
  const handleClearManualSelection = () => {
    console.log('Resuming automatic lyric tracking');
    setManualLyricSelection(null, null);
  };

  // Function to handle resuming auto tracking
  const handleResumeAutoTracking = () => {
    // Reset manual selection states
    clearManualStates();
    
    // Reset user scrolling state to allow auto-scrolling to resume
    setUserScrolling(false);

    // If we have playback state, reset the progress to the current position
    if (playbackState) {
      const currentPosition = progressMs; // Use our current progress
      
      // Restart the progress timer from the current position
      if (playbackState.is_playing) {
        startProgressTimer(currentPosition, true);
      } else {
        setProgressMs(currentPosition);
      }
      
      // Immediately trigger a lyrics update based on the current position
      const lyricUpdateTimer: ReturnType<typeof setTimeout> = setTimeout(() => {
        getCurrentLyric();
      }, 50);
    }
  };

  // Get the current lyric line, accounting for manual selection
  const getCurrentLyric = useCallback(() => {
    if (!lyrics || !lyrics.syncedLyrics || !playbackState?.item?.id) {
      setCurrentLyricLine("");
      setCurrentLyricTimestamp(undefined);
      return;
    }
    
    // Sort the timestamps from the synced lyrics
    const sortedTimes = Object.keys(lyrics.syncedLyrics)
      .map(time => parseInt(time))
      .sort((a, b) => a - b);
    
    if (sortedTimes.length === 0) return;
    
    // Current progress from progress timer with lyrics delay applied
    // Use getEffectiveProgress() to include the delay
    const effectiveProgress = getEffectiveProgress();
    
    // Find the current line based on progress with delay applied
    let currentIndex = 0;
    for (let i = 0; i < sortedTimes.length; i++) {
      if (effectiveProgress >= sortedTimes[i]) {
        currentIndex = i;
      } else {
        break;
      }
    }
    
    const currentTime = sortedTimes[currentIndex];
    const lyricText = lyrics.syncedLyrics[currentTime.toString()];
    
    // Only override with manual selection if we're in manual selection mode
    if (manualSelectedLyric !== null && manualLyricTimestamp !== null) {
      setCurrentLyricLine(manualSelectedLyric);
      setCurrentLyricTimestamp(manualLyricTimestamp);
    } else {
      setCurrentLyricLine(lyricText);
      setCurrentLyricTimestamp(currentTime);
    }
  }, [lyrics, progressMs, playbackState?.item?.id, manualSelectedLyric, manualLyricTimestamp, getEffectiveProgress]);

  // Add a function to handle refresh button click
  const handleRefresh = async () => {
    console.log('Manually refreshing token...');
    setIsAuthenticating(true);
    try {
      await forceSyncWithService();
      trackButtonClick('Refresh', 'Player');
    } catch (error) {
      console.error('Error refreshing:', error);
      trackError('Player', 'Refresh failed', false);
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Effect to update the current lyrics line based on playback progress
  useEffect(() => {
    if (playbackState) {
      let timeoutId: ReturnType<typeof setTimeout>;
      
      const updateCurrentLyric = () => {
        // Only update if we have the necessary data
        if (lyrics?.synced && lyrics?.syncedLyrics) {
          // When in manual mode, we still need to let the progress timer run,
          // but don't update the current lyric
          if (manualSelectedLyric === null) {
            getCurrentLyric();
          } else {
            // In manual mode, just make sure the current lyric stays on the manual selection
            setCurrentLyricLine(manualSelectedLyric);
            if (manualLyricTimestamp !== null) {
              setCurrentLyricTimestamp(manualLyricTimestamp);
            }
          }
          
          // Schedule the next update regardless - this ensures the timer keeps running
          if (playbackState?.is_playing) {
            timeoutId = setTimeout(updateCurrentLyric, 200);
          }
        }
      };
      
      // Start updating
      updateCurrentLyric();
      
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, [playbackState, lyrics, getCurrentLyric, manualSelectedLyric, manualLyricTimestamp]);

  // Additional effect to update lyrics when progress changes
  useEffect(() => {
    // Only update lyric based on progress if not in manual selection mode
    if (lyrics?.synced && lyrics?.syncedLyrics && manualSelectedLyric === null) {
      getCurrentLyric();
    }
  }, [progressMs, lyrics, getCurrentLyric, manualSelectedLyric]);

  // Add useEffect for auto-scrolling to the current lyric
  useEffect(() => {
    if (currentLineRef.current && lyrics?.syncedLyrics) {
      currentLineRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentLyricLine, lyrics?.syncedLyrics]);

  // Fetch current track based on selected service
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let noSongPlaying = false;
    let lastProgressMs: number | null = null;
    let lastDriftResetTime = 0; // Track when we last did a drift reset
    let isResetting = false; // Prevent multiple simultaneous resets

    const fetchPlaybackState = async () => {
      if (isSpotifySelected && spotifyToken) {
        try {
          const state = await spotifyService.getCurrentTrack(spotifyToken);
          
          // If no track is playing, show a message and retry after 3 seconds
          if (!state || !state.item) {
            console.log('No song playing. Will retry in 3 seconds.');
            setPlaybackState({ 
              is_playing: false, 
              noSongPlaying: true,
              item: null,
              progress_ms: 0
            });
            setLyrics(null);
            setCurrentLyricLine('');
            setCurrentTrackId(null);
            
            // Clear existing timeout if any
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
            
            // Set flag to avoid setting interval
            noSongPlaying = true;
            
            // Set timeout for retry after 3 seconds
            timeoutId = setTimeout(fetchPlaybackState, 3000);
            return;
          }
          
          // Reset no song playing flag
          noSongPlaying = false;
          
          if (state) {
            // Extract API latency from the response
            const apiLatency = state._apiLatency || 0;
            
            // Update minimum latency if this response was faster
            if (apiLatency > 0 && (apiLatency < minApiLatency || minApiLatency === 0)) {
              setMinApiLatency(apiLatency);
            }
            
            // Check if the track has changed - simpler and more reliable check
            const trackIdChanged = playbackState?.item?.id !== state.item?.id && 
                                   state.item?.id !== currentTrackId;
            
            // If track hasn't changed, we need to be more careful about refreshes
            const trackRestarted = !trackIdChanged && playbackState?.item?.id === state.item?.id && 
              playbackState.progress_ms > 10000 && state.progress_ms < 3000;
            
            // Print debug info only for significant progress issues
            const apiProgressMs = state.progress_ms;
            
            // Check if we're getting consecutive identical progress values when is_playing is false
            // This indicates the track is actually paused even if our local progress is advancing
            if (!state.is_playing && lastProgressMs !== null && apiProgressMs === lastProgressMs) {
              console.log(`Detected consecutive identical progress values (${apiProgressMs}ms) with is_playing=false. Pausing local progress.`);
              // Force pause the progress timer AND explicitly set progress to match the API value
              // This ensures our local progress doesn't continue to advance
              startProgressTimer(apiProgressMs, false);
              // Explicitly set progress to match API to fix any discrepancy
              setProgressMs(apiProgressMs);
              // Update playback state to ensure consistency across the app
              setPlaybackState({
                ...state,
                progress_ms: apiProgressMs // Override with API value
              });
              // Track that we've handled this paused state to avoid duplicate actions
              lastProgressMs = apiProgressMs;
              // Exit early to prevent further processing that might override these changes
              return;
            }
            
            // Always update the last progress value for the next check
            lastProgressMs = apiProgressMs;
            
            // Always use the minimum observed latency for more accurate adjustments
            const adjustedApiProgressMs = apiProgressMs;
            
            const progressDiff = Math.abs(progressMs - adjustedApiProgressMs);
            const significantProgressDiff = progressDiff > 4000;  // 3-second threshold
            
            // Check if play state changed
            const playStateChanged = playbackState?.is_playing !== state.is_playing;
            
            // Check if we're in ANY kind of manual mode (either manual lyric selection or delay)
            const inManualMode = isInManualMode();
            
            // In manual mode, don't log the progress diffs to reduce noise
            if ((significantProgressDiff || trackIdChanged || trackRestarted) && !inManualMode) {
              console.log(`Progress check - Current: ${progressMs}ms, API: ${apiProgressMs}ms, API Latency: ${apiLatency}ms, Min Latency: ${minApiLatency}ms, Adjusted API: ${adjustedApiProgressMs}ms, Diff: ${progressDiff}ms`);
            }
            
            // Check if client progress is ahead of API progress
            const clientAheadOfApi = progressMs > (adjustedApiProgressMs + 3000);
            const clientBehindApi = adjustedApiProgressMs > (progressMs + 3000);
            
            // Determine if the difference is extreme (more than 30 seconds)
            const extremeDifference = progressDiff > 30000;
            
            // Check if we should ignore the API progress (client is way ahead but song is still playing correctly)
            // Only ignore if the song is currently playing and we're not at an extreme difference
            const shouldIgnoreApiProgress = clientAheadOfApi && state.is_playing && !extremeDifference;
            
            if (clientAheadOfApi && !inManualMode && !shouldIgnoreApiProgress) {
              console.log(`Client progress (${progressMs}ms) is ahead of API (${adjustedApiProgressMs}ms) - refreshing lyrics panel`);
              // Force re-render of lyrics component
              setLyricsKey(Date.now());
            }
            
            // Only update the UI if:
            // 1. The track ID actually changed
            // 2. The track was restarted/seeked considerably  
            // 3. There's a significant progress difference (> 3 seconds) AND not in manual mode AND NOT when client is ahead
            // 4. The playing/paused state changed
            // 5. *** NEW: We received a faster API response with lower latency ***
            const fasterResponse = apiLatency > 0 && apiLatency <= minApiLatency;
            
            if (trackIdChanged || trackRestarted || 
                (significantProgressDiff && !inManualMode && !shouldIgnoreApiProgress) || 
                playStateChanged || (fasterResponse && !inManualMode && state.is_playing)) {
              // Only log state updates if not in manual mode or if it's an important change
              if (!inManualMode || trackIdChanged || playStateChanged) {
                console.log(
                  `Updating playback state: ${trackIdChanged ? 'track ID changed ' : ''}` +
                  `${trackRestarted ? 'track restarted ' : ''}` +
                  `${significantProgressDiff && !shouldIgnoreApiProgress ? 'significant progress diff ' : ''}` +
                  `${playStateChanged ? 'play state changed ' : ''}` +
                  `${fasterResponse ? 'faster response received ' : ''}` +
                  `${shouldIgnoreApiProgress ? 'IGNORING API PROGRESS - client ahead' : ''}`
                );
              }
              
              // Update the playback state but preserve our progress in manual mode or when we should ignore API progress
              if (!inManualMode && !shouldIgnoreApiProgress) {
                setPlaybackState(state);
              } else {
                // In manual mode or when client is ahead, update state but preserve our progress
                setPlaybackState((prevState: any) => ({
                  ...state,
                  progress_ms: progressMs // Keep our local progress
                }));
              }
              
              // Adjust the timer if needed
              if ((significantProgressDiff && !shouldIgnoreApiProgress) || 
                  playStateChanged || trackRestarted || fasterResponse) {
                if (!inManualMode && state.is_playing && !shouldIgnoreApiProgress) {
                  console.log(`Adjusting progress timer to ${adjustedApiProgressMs}ms (API: ${apiProgressMs}ms, MinLatency: ${minApiLatency}ms, Diff: ${progressDiff}ms)`);
                  
                  // Use resetForNewTrack for significant drift or periodically to prevent accumulation
                  const now = Date.now();
                  const timeSinceLastReset = now - lastDriftResetTime;
                  const shouldPeriodicReset = timeSinceLastReset > 60000; // Reset every 60 seconds
                  const largeDrift = Math.abs(progressDiff) > 5000; // 5 second threshold for immediate reset
                  
                  if ((largeDrift || shouldPeriodicReset) && !isResetting) {
                    const resetReason = largeDrift ? `Large drift detected (${progressDiff}ms)` : `Periodic drift prevention (${Math.round(timeSinceLastReset/1000)}s since last reset)`;
                    console.log(`${resetReason}, using resetTimingForSameTrack`);
                    isResetting = true;
                    resetTimingForSameTrack(adjustedApiProgressMs, state.is_playing, now);
                    lastDriftResetTime = now;
                    // Clear reset flag after a delay to prevent rapid resets
                    setTimeout(() => {
                      isResetting = false;
                    }, 1000);
                  } else if (!isResetting) {
                    startProgressTimer(adjustedApiProgressMs, state.is_playing);
                  } else {
                    console.log(`Skipping timer adjustment - reset in progress`);
                  }
                } else if (shouldIgnoreApiProgress) {
                  console.log(`NOT adjusting progress timer - client is ahead but song is playing correctly. Current: ${progressMs}ms, API: ${adjustedApiProgressMs}ms`);
                  // Continue playing from current position
                  if (!isPlaying && state.is_playing) {
                    startProgressTimer(progressMs, true);
                  }
                } else if (inManualMode) {
                  console.log(`Not adjusting timer - in manual mode (${manualSelectedLyric ? 'manual selection' : 'delay mode'}, Current: ${progressMs}ms)`);
                  
                  // If we're playing but in manual mode, ensure the timer continues
                  if (state.is_playing) {
                    console.log(`Ensuring progress timer continues in manual mode`);
                    startProgressTimer(progressMs, true);
                  }
                }
              }
            } else {
              // Silent update to maintain state without triggering UI refresh
              setPlaybackState((prevState: any) => ({
                ...state,
                // Keep our local progress to avoid jitter
                progress_ms: progressMs
              }));
            }
            
            // Handle lyrics fetching - ONLY when the track ID has actually changed
            if (trackIdChanged && state.item && state.item.id) {
              // Additional verification to avoid duplicate fetches
              if (currentTrackId !== state.item.id) {
                setIsLoading(true);
                setIsChangingSong(true);
                console.log(`Track changed from ${currentTrackId} to ${state.item.id}`);
                console.log(`Fetching lyrics for: "${state.item.name}" by ${state.item.artists[0].name}`);
                
                // Reset scroll state when track changes
                setUserScrolling(false);
                
                // HARD RESET for new track - clears all context state
                console.log(`NEW SONG DETECTED: Using hard reset for track change`);
                resetForNewTrack(state.item.id, adjustedApiProgressMs, state.is_playing, Date.now());
                
                try {
                  const trackLyrics = await spotifyService.getLyrics(spotifyToken, state.item.id);
                  console.log('Lyrics fetched successfully:', {
                    source: trackLyrics.source,
                    synced: trackLyrics.synced,
                    hasSyncedData: trackLyrics.syncedLyrics ? Object.keys(trackLyrics.syncedLyrics).length : 0
                  });
                  
                  setLyrics(trackLyrics);
                } catch (lyricsError) {
                  console.error('Error fetching lyrics:', lyricsError);
                  setLyrics({
                    lyrics: `Error fetching lyrics for "${state.item.name}". Please try again later.`,
                    source: 'error',
                    synced: false
                  });
                } finally {
                  // Add a slight delay before removing loading states to allow for a smoother transition
                  setTimeout(() => {
                    setIsLoading(false);
                    setTimeout(() => setIsChangingSong(false), 300);
                  }, 500);
                }
              } else {
                console.log('Track ID matches current ref, avoiding duplicate lyrics fetch');
              }
            }
          } else {
            setPlaybackState(null);
            setLyrics(null);
            setCurrentLyricLine('');
            setCurrentTrackId(null);
          }
        } catch (error) {
          console.error('Error fetching track data:', error);
          setPlaybackState(null);
          setLyrics(null);
          setCurrentLyricLine('');
          setCurrentTrackId(null);
          
          // On error, also retry after 3 seconds
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          noSongPlaying = true;
          timeoutId = setTimeout(fetchPlaybackState, 3000);
        }
      } else if (isAppleSelected && appleMusicInstance) {
        try {
          // Check if Apple Music is authenticated
          if (!isAppleAuthenticated) {
            console.log('Apple Music not authenticated yet');
            return;
          }
          
          const state = await appleMusicService.getCurrentTrack(appleMusicInstance);
          
          // If state is null, it means either:
          // 1. No track is playing, or
          // 2. The track hasn't changed since last check
          if (!state) {
            // Do nothing - keep the current state
            return;
          }
          
          // Check if we're in ANY kind of manual mode
          const inManualMode = isInManualMode();
          
          // Check if this is a new track
          const trackIdChanged = state.trackChanged === true;
          
          // Only update UI and fetch lyrics if the track has changed
          if (inManualMode && !trackIdChanged) {
            console.log('In manual mode - not updating from Apple Music API unless track changes');
            
            // Update the state but preserve our progress
            setPlaybackState((prevState: any) => ({
              ...state,
              progress_ms: progressMs // Keep our local progress
            }));
            
            // If playing, ensure the timer continues
            if (state.is_playing) {
              console.log(`Ensuring progress timer continues in manual mode`);
              startProgressTimer(progressMs, true);
            }
          } else {
            // Normal update (not in manual mode or track changed)
            setPlaybackState(state);
            setIsLoading(true);
            setIsChangingSong(true);
            
            // Start or update the progress timer
            // Since we don't know the actual progress, we'll just start from the beginning
            startProgressTimer(0, true);

            if (state.item && state.item.id) {
              console.log(`Fetching lyrics for: "${state.item.name}" by ${state.item.artists[0].name}`);
              
              // Reset scroll state when track changes
              setUserScrolling(false);
              
              try {
                const trackLyrics = await appleMusicService.getLyrics(appleMusicInstance, state.item.id);
                setLyrics(trackLyrics);
              } catch (lyricsError) {
                console.error('Error fetching lyrics:', lyricsError);
                setLyrics({
                  lyrics: `Error fetching lyrics for "${state.item.name}". Please try again later.`,
                  source: 'error',
                  synced: false
                });
              }
            }
            // Add a slight delay before removing loading states to allow for a smoother transition
            setTimeout(() => {
              setIsLoading(false);
              setTimeout(() => setIsChangingSong(false), 300);
            }, 500);
          }
        } catch (error) {
          console.error('Error fetching track data:', error);
          setPlaybackState(null);
          setLyrics(null);
          setCurrentLyricLine('');
          setCurrentTrackId(null);
        }
      }
    };

    if (isAuthenticated) {
      fetchPlaybackState();
      // Only set interval if not in "no song playing" state
      interval = setInterval(() => {
        if (!noSongPlaying) {
          fetchPlaybackState();
        }
      }, 5000);
      
      // Reset the current track when switching services or logging in
      if (isAppleSelected) {
        appleMusicService.resetCurrentTrack();
        setCurrentTrackId(null);
      } else if (isSpotifySelected) {
        spotifyService.resetCurrentTrack();
        setCurrentTrackId(null);
      }
    } else {
      setPlaybackState(null);
      setLyrics(null);
      setCurrentTrackId(null);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isAuthenticated, spotifyToken, appleMusicInstance, isSpotifySelected, isAppleSelected, isAppleAuthenticated, setCurrentTrackId, startProgressTimer]);

  // Function to fetch user's queue based on selected service
  const fetchQueue = useCallback(async () => {
    if (isSpotifySelected && spotifyToken) {
      setLoadingQueue(true);
      try {
        const queue = await spotifyService.getQueue(spotifyToken);
        console.log('Queue data:', queue);
        setQueueData(queue);
      } catch (error) {
        console.error('Error fetching queue:', error);
      } finally {
        setLoadingQueue(false);
      }
    } else if (isAppleSelected && appleMusicInstance) {
      setLoadingQueue(true);
      try {
        const queue = await appleMusicService.getQueue(appleMusicInstance);
        console.log('Apple Music queue data:', queue);
        setQueueData(queue);
      } catch (error) {
        console.error('Error fetching Apple Music queue:', error);
      } finally {
        setLoadingQueue(false);
      }
    }
  }, [isSpotifySelected, spotifyToken, isAppleSelected, appleMusicInstance]);

  // Fetch queue when tab changes to queue
  useEffect(() => {
    if (activeTab === 1 && isAuthenticated) {
      fetchQueue();
    }
  }, [activeTab, isAuthenticated, fetchQueue]);

  // Add a key to force re-render of the lyrics component
  const [lyricsKey, setLyricsKey] = useState(Date.now());

  // Add a renderLyrics function
  const renderLyrics = () => {
    return (
      <Box position="relative" display="flex" flexDirection="column" height="100%">
        <MusicServiceToggle
          isSpotifySelected={isSpotifySelected}
          isAppleSelected={isAppleSelected}
          isSpotifyAuthenticated={isSpotifyAuthenticated}
          isAppleAuthenticated={isAppleAuthenticated}
          handleServiceChange={handleServiceChange}
          onSyncProgress={forceSyncWithService}
        />
        <Box flex="1" overflow="auto">
          <Lyrics
            key={lyricsKey}
            lyrics={lyrics}
            progressMs={progressMs + lyricsDelay}
            isActive={playbackState?.is_playing}
            onManualSelect={handleManualLyricSelect}
            currentLyricLine={currentLyricLine}
            currentLyricTimestamp={currentLyricTimestamp}
            isLoading={false}
            textColor="var(--color-text-primary)"
            mutedTextColor="var(--color-text-muted)"
            currentTrackId={playbackState?.item?.id}
            onResumeAutoTracking={handleResumeAutoTracking}
            onSettingsOpen={onOpen}
            frostedEffect={frostedEffect}
            frostedOpacity={frostedOpacity}
            spotifyToken={isSpotifySelected ? spotifyToken : null}
            isSpotifyActive={isSpotifySelected}
          />
        </Box>
      </Box>
    );
  };

  // Render the appropriate layout based on layoutMode
  const renderLayout = () => {
    // Get duration safely
    const duration = playbackState?.item?.duration_ms || 0;
    const albumImageUrl = playbackState?.item?.album?.images?.[0]?.url;
    
    // Full Width layout
    if (layoutMode === 'tesla') {
      return (
        <>
          <Background albumImageUrl={albumImageUrl} />
          <TeslaLayout
            lyricsContent={renderLyrics()}
            playbackState={playbackState}
            progressMs={progressMs}
            duration={duration}
            startProgressTimer={startProgressTimer}
            setProgressMs={setProgressMs}
            accentColor={accentColor as any}
            lyricsDelay={lyricsDelay}
            setLyricsDelay={setLyricsDelay}
            layoutMode={layoutMode}
            setLayoutMode={setLayoutMode}
          />
        </>
      );
    }
    
    // Default to split layout
    return (
      <>
        <Background albumImageUrl={albumImageUrl} />
        <SideBySideLayout
          albumImageUrl={albumImageUrl}
          lyricsContent={renderLyrics()}
          playbackState={playbackState}
          progressMs={progressMs}
          duration={duration}
          startProgressTimer={startProgressTimer}
          setProgressMs={setProgressMs}
          accentColor={accentColor as any}
          lyricsDelay={lyricsDelay}
          setLyricsDelay={setLyricsDelay}
          layoutMode={layoutMode}
          setLayoutMode={setLayoutMode}
        />
      </>
    );
  };

  const [username, setUsername] = useState<string | null>(null);

  useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setUsername(session.user?.email?.split('@')[0] || null);
      }
    };
    
    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUsername(session?.user?.email?.split('@')[0] || null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Add a function to force sync with the music service
  const forceSyncWithService = async () => {
    console.log('Forcing sync with music service...');
    
    try {
      if (isSpotifySelected && spotifyToken) {
        // Show a loading indicator
        toast({
          title: "Syncing...",
          description: "Syncing with Spotify",
          status: "info",
          duration: 2000,
          isClosable: true,
        });
        
        const state = await spotifyService.getCurrentTrack(spotifyToken);
        if (state) {
          console.log(`Synced with Spotify. Progress: ${state.progress_ms}ms`);
          
          // Update the playback state
          setPlaybackState(state);
          
          // Account for API latency and update progress timer
          const apiLatency = state._apiLatency || minApiLatency;
          const adjustedApiProgressMs = state.progress_ms
          
          // Reset manual states if any
          clearManualStates();
          
          // Use resetForNewTrack for manual sync to ensure clean timing reset
          resetForNewTrack(state.item.id, adjustedApiProgressMs, state.is_playing, Date.now());
          
          // Force re-render of lyrics component by updating its key
          setLyricsKey(Date.now());
          
          toast({
            title: "Synced",
            description: `Progress updated to ${formatDuration(adjustedApiProgressMs)}`,
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        }
      } else if (isAppleSelected && appleMusicInstance) {
        // Show a loading indicator
        toast({
          title: "Syncing...",
          description: "Syncing with Apple Music",
          status: "info",
          duration: 2000,
          isClosable: true,
        });
        
        const state = await appleMusicService.getCurrentTrack(appleMusicInstance, true);
        if (state) {
          console.log(`Synced with Apple Music. Progress: ${state.progress_ms}ms`);
          
          // Update the playback state
          setPlaybackState(state);
          
          // For Apple Music, we take the progress as is
          
          // Reset manual states if any
          clearManualStates();
          
          // Update the progress timer
          startProgressTimer(state.progress_ms, state.is_playing);
          
          // Force re-render of lyrics component by updating its key
          setLyricsKey(Date.now());
          
          toast({
            title: "Synced",
            description: `Progress updated to ${formatDuration(state.progress_ms)}`,
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error syncing with music service:', error);
      toast({
        title: "Sync Failed",
        description: "Could not sync with music service",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Add modal state for auth modal
  const { isOpen: isAuthModalOpen, onOpen: onOpenAuthModal, onClose: onCloseAuthModal } = useDisclosure();

  // Effect to check authentication and open modal if needed
  useEffect(() => {
    if (isSupabaseAuthenticated && isMusicServiceAuthenticated === false) {
      onOpenAuthModal();
    }
  }, [isSupabaseAuthenticated, isMusicServiceAuthenticated, onOpenAuthModal]);

  // Track playback state changes
  useEffect(() => {
    if (playbackState?.item || playbackState?.nowPlayingItem) {
      const songInfo = isSpotifySelected 
        ? {
            title: playbackState?.item?.name || 'Unknown',
            artist: playbackState?.item?.artists?.[0]?.name || 'Unknown',
            service: 'Spotify'
          }
        : {
            title: playbackState?.nowPlayingItem?.title || 'Unknown',
            artist: playbackState?.nowPlayingItem?.artistName || 'Unknown',
            service: 'Apple Music'
          };
      
      // Track song play event
      trackSongPlay(songInfo.title, songInfo.artist, songInfo.service);
      
      // Track lyrics view if we have lyrics
      if (lyrics) {
        trackLyricsView(songInfo.title, songInfo.artist);
      }
    }
  }, [playbackState?.item?.id, playbackState?.nowPlayingItem?.id, lyrics]);

  // Add auth modal component
  if (!isAuthenticated) {
    return (
      <Box
        minH="100vh"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        bg="var(--color-bg-primary)"
        color="var(--color-text-primary)"
        px={4}
      >
        <VStack spacing={8} align="center" maxW="md">
          <Image
            src="/logo.png"
            alt="SyncdIt Logo"
            boxSize="100px"
            mb={4}
          />
          <Heading size="xl" fontWeight="bold" textAlign="center">
            Welcome to SyncdIt
          </Heading>
          <Text fontSize="lg" textAlign="center">
            Connect your music service to see synchronized lyrics
          </Text>
          
          <VStack spacing={6} width="100%" mt={4}>
            <Button
              size="lg"
              width="full"
              colorScheme="green"
              leftIcon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.4 0 0 5.4 0 12C0 18.6 5.4 24 12 24C18.6 24 24 18.6 24 12C24 5.4 18.66 0 12 0ZM17.521 17.34C17.281 17.699 16.861 17.819 16.5 17.58C13.68 15.801 10.14 15.42 5.939 16.38C5.521 16.5 5.1 16.26 4.98 15.84C4.86 15.42 5.1 15 5.52 14.88C10.14 13.8 14.1 14.25 17.28 16.26C17.639 16.5 17.76 16.98 17.52 17.34H17.521ZM18.961 14.04C18.66 14.46 18.12 14.64 17.7 14.34C14.46 12.36 9.72 11.76 5.82 12.9C5.34 13.02 4.86 12.78 4.74 12.3C4.62 11.82 4.86 11.34 5.34 11.22C9.9 9.96 15.12 10.62 18.84 12.9C19.201 13.14 19.38 13.74 18.96 14.04H18.961ZM19.08 10.68C15.18 8.4 8.82 8.16 5.16 9.42C4.56 9.6 3.96 9.24 3.78 8.64C3.6 8.04 3.96 7.44 4.56 7.26C8.82 5.76 15.9 6 20.34 8.64C20.88 8.94 21.06 9.66 20.76 10.14C20.46 10.62 19.74 10.86 19.26 10.56C19.2 10.62 19.14 10.68 19.08 10.68Z" fill="currentColor"/>
                </svg>
              }
              onClick={() => {
                spotifyLogin();
                setIsAuthenticating(true);
              }}
              isLoading={isAuthenticating}
            >
              Connect with Spotify
            </Button>
            
            <Button
              size="lg"
              width="full"
              colorScheme="pink"
              leftIcon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.9947 18.19C23.4707 19.411 22.9947 20.421 22.3547 21.579C21.5227 23.133 20.8387 24 19.5787 24C18.7387 24 18.0307 23.667 17.1987 23.332C16.3347 22.974 15.4227 22.604 14.1547 22.604C12.8107 22.604 11.8747 22.974 10.9987 23.334C10.1787 23.667 9.43475 24 8.67475 24C7.32475 24 6.59875 23.057 5.69075 21.471C4.58675 19.579 3.67475 16.911 3.67475 14.357C3.67475 12.151 4.34675 10.269 5.42275 8.996C6.30575 7.94 7.63275 7.241 9.01275 7.241C9.91675 7.241 10.6987 7.581 11.3907 7.883C12.0347 8.168 12.6027 8.423 13.1867 8.423C13.6707 8.423 14.2227 8.168 14.8547 7.886C15.5947 7.56 16.4547 7.184 17.4947 7.218C18.5427 7.25 19.9467 7.604 20.9787 8.76C19.5467 9.781 18.7467 11.24 18.7467 12.766C18.7467 14.55 19.5867 16.045 21.0867 16.971C21.7627 17.409 22.4947 17.697 23.3107 17.83C23.5467 17.947 23.7787 18.066 23.9947 18.19ZM17.3547 0C17.4547 2.103 16.5147 4.142 15.1387 5.531C13.7547 6.92 11.8667 7.629 10.1387 7.47C9.98675 5.46 10.9987 3.357 12.2947 2.069C13.7227 0.643 15.6827 0 17.3547 0Z" fill="currentColor"/>
                </svg>
              }
              onClick={() => {
                appleLogin();
                setIsAuthenticating(true);
              }}
              isLoading={isAuthenticating || isAppleMusicInitializing}
              loadingText={isAppleMusicInitializing ? "Initializing..." : "Connecting..."}
            >
              Connect with Apple Music
            </Button>
            
            {authError && (
              <Text color="red.500" fontSize="sm">
                {authError}
              </Text>
            )}
          </VStack>
          
          {spotifyTokenExpired && (
            <Button
              mt={4}
              size="sm"
              variant="outline"
              colorScheme="green"
              leftIcon={<Icon as={FiRefreshCw} />}
              onClick={handleRefresh}
              isLoading={isAuthenticating}
            >
              Refresh Spotify Token
            </Button>
          )}
        </VStack>
      </Box>
    );
  }
  
  return (
    <>
      <Box className="app-container" position="relative" bg="var(--color-bg-primary)" color="white">
        <AppHeader 
          pageTitle={`Player: ${playbackState?.item?.name}`}
          playbackState={playbackState}
          layoutMode={layoutMode}
          setLayoutMode={setLayoutMode}
          accentColor="#1DB954"
          isCondensed={true}
        />
        {renderLayout()}

        <LyricsSettingsModal 
          isOpen={isOpen} 
          onClose={onClose} 
          layoutMode={layoutMode}
          setLayoutMode={setLayoutMode} 
        />
        
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={onCloseAuthModal}
          isSpotifyAuthenticated={isSpotifyAuthenticated}
          isAppleAuthenticated={isAppleAuthenticated}
          spotifyLogin={spotifyLogin}
          appleLogin={appleLogin}
          isAuthenticating={isAuthenticating}
          authError={authError}
          isAppleMusicInitializing={isAppleMusicInitializing}
        />
      </Box>
    </>
  );
};

export default Player; 